// @ts-nocheck

import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { InfoFilledSvg } from '../../assets/svg';
import { Target } from '../../client';
import { __ } from '../../i18n/l10n';
import LineUpRouterSwitch from '../../modules/line-up/components/LineUpRouterSwitch/LineUpRouterSwitch';
import HoF from '../../modules/match-ended/components/HallOfFame';
import MissionBannerContainer from '../../modules/missions/components/MissionBanner/MissionBannerContainer';
import { type MatchState, MatchEntityStatus } from '../../states/entities/matches';
import theme from '../../styled-components/dark';
import { MatchViewContext } from '../../utils/contexts';
import MatchChip from '../MatchChip';
import MatchGroupsWithBetFiltersContainer from '../MatchGroupsWithBetFilters/MatchGroupsWithBetFiltersContainer';
import MatchHeaderContainer from '../MatchHeader/MatchHeaderContainer';
import MatchToolbarContainer from '../MatchToolbar/MatchToolbarContainer';
import MultimatchDetailsContainer from '../MultimatchDetails/MultimatchDetailsContainer';
import MyMatchErrorPopUpContainer from '../MyMatchErrorPopUp/MyMatchErrorPopUpContainer';
import Tabs from '../Tabs';
import WinaLoader from '../WinaLoader';

const PromoWrapper = styled.div`
  padding: 0 0.7em;

  &&&& {
    color: white;
    > * {
      padding: 0 0 10px;
    }
    > a {
      width: 100%;
      padding: 0;
      display: block;
    }
    > img {
      max-width: 100%;
      display: block;
      padding: 0;
    }
    > a > img {
      max-width: 100%;
      border-radius: 1em;
    }
  }
`;

const StyledMatchToolbarContainer = styled(MatchToolbarContainer)`
  border-top: solid 1px ${theme.Match.Separator.backgroundColor};
`;

const StyledMissionBannerContainer = styled(MissionBannerContainer)`
  padding: 0 0.75em;
`;

const ResultTabs = styled.div`
  font-size: 1em;
  margin: 0.6em;
`;

const MatchBestBet = styled(HoF)`
  margin: 0;
`;

const MatchBetFilter = styled(MatchGroupsWithBetFiltersContainer)`
  margin: 0;
`;

const HiddenByWidget = styled.div`
  margin-bottom: 0.6em;
`;

const StyledWinaLoader = styled(WinaLoader)``;

const MatchChipStatus = styled(MatchChip)`
  margin: 0.6em;
`;

const StyledMatchHeaderContainer = styled(MatchHeaderContainer)``;

const TABS = {
  bets: {
    id: 'bets',
  },
  odds: {
    id: 'odds',
  },
};
type MatchProps = MatchState & {
  sportPromo?: string;
  categoryPromo?: string;
  tournamentPromo?: string;
  hallOfFameBetslips?: Array<unknown>;
  missionId?: number;
  hasOdds?: boolean;
  isMultimatch?: boolean;
};

const Match = ({
  matchId,
  status,
  sportPromo,
  categoryPromo,
  tournamentPromo,
  matchPromo,
  missionId,
  hallOfFameBetslips,
  hasOdds,
  isOutright = false,
  isMultimatch,
}: MatchProps) => {
  const [selectedTab, setSelectedTab] = useState();
  const [widgetSize, setWidgetSize] = useState(0);
  const [headerSize, setHeaderSize] = useState(0);
  const [initialHeaderSize, setInitialHeaderSize] = useState(0);
  const [promoSize, setPromoSize] = useState(0);
  const [toolbarSize, setToolbarSize] = useState(0);
  const [isWidgetSticky, setIsWidgetSticky] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  const isWidgetOpen = useMemo(() => !!widgetSize, [widgetSize]);
  const stickyHeaderSize = useMemo(() => (isHeaderSticky ? headerSize : 0), [isHeaderSticky, headerSize]);
  const isEnded = useMemo(() => MatchEntityStatus.isEnded(status), [status]);

  const measuredPromoHeight = useCallback(
    (node) => {
      if (node && missionId !== undefined) {
        const { height } = node.getBoundingClientRect();
        setPromoSize(height);
      }
    },
    [missionId],
  );

  const Promotion = useMemo(() => {
    if (sportPromo || categoryPromo || tournamentPromo || matchPromo) {
      return (
        (matchPromo || tournamentPromo || categoryPromo || sportPromo) && (
          <PromoWrapper
            dangerouslySetInnerHTML={{ __html: matchPromo || tournamentPromo || categoryPromo || sportPromo }}
          />
        )
      );
    }
    return null;
  }, [categoryPromo, matchPromo, sportPromo, tournamentPromo]);

  const tabItems = useMemo(() => {
    const tabItems = [];

    if (hallOfFameBetslips) {
      tabItems.push({
        ...TABS.bets,
        label: __('Best bets'),
      });
    }

    if (hasOdds) {
      tabItems.push({
        ...TABS.odds,
        label: __('Match odds'),
      });
    }

    if (tabItems.length > 0) {
      setSelectedTab(tabItems[0].id);
    }

    return tabItems;
  }, [hasOdds, hallOfFameBetslips]);

  const handleTabChange = useCallback((id) => {
    setSelectedTab(id);
  }, []);

  const renderMatchEndedTabs = useMemo(() => {
    if (selectedTab === TABS.bets.id && hallOfFameBetslips) {
      return <MatchBestBet matchId={matchId} hallOfFameBetslipsIds={hallOfFameBetslips} />;
    }

    if (selectedTab === TABS.odds.id) {
      return <MatchBetFilter />;
    }

    return null;
  }, [hallOfFameBetslips, matchId, selectedTab]);

  // --------------- PREMATCH ---------------------
  // [icon] [category + tournament + round] [fav]
  // [title]
  // [date] [tvchannel]
  // [promo]
  // [warning]
  // [toolbar]
  // --------------- LIVE -------------------------
  // [icon] [category + tournament + round] [fav]
  // [title]
  // [status] [tvchannel]
  // [promo]
  // [warning]
  // [live widget]
  // [toolbar]
  // --------------- LIVE OUTRIGHT ---------------
  // [icon] [category + tournament + round] [fav]
  // [title]
  // [status] [tvchannel]
  // [promo]
  // [warning]
  // [toolbar]
  // ---------------------------------------------

  const forwardedState = useMemo(
    () => ({
      matchId,

      promoSize,
      setPromoSize,

      toolbarSize,
      setToolbarSize,

      widgetSize,
      setWidgetSize,
      setIsWidgetSticky,
      isWidgetSticky,

      stickyHeaderSize,

      initialHeaderSize,
      setInitialHeaderSize,
      headerSize,
      setHeaderSize,
      isHeaderSticky,
      setIsHeaderSticky,
    }),
    [
      matchId,
      promoSize,
      toolbarSize,
      widgetSize,
      isWidgetSticky,
      stickyHeaderSize,
      initialHeaderSize,
      headerSize,
      isHeaderSticky,
    ],
  );

  if (matchId) {
    return (
      <MatchViewContext.Provider value={forwardedState}>
        {isEnded ? (
          <MatchChipStatus icon={<InfoFilledSvg />} color="blue" size="large">
            {isOutright ? __('Event ended') : __('Match ended')}
          </MatchChipStatus>
        ) : null}
        <StyledMatchHeaderContainer matchId={matchId} allowStickiness />
        {/* Toolbar with streaming and trackers */}
        <StyledMatchToolbarContainer stickyContent />
        {!isWidgetOpen ? (
          <HiddenByWidget ref={measuredPromoHeight}>
            {Promotion}
            {missionId && !isEnded ? <StyledMissionBannerContainer missionId={missionId} /> : null}
          </HiddenByWidget>
        ) : null}
        {isMultimatch ? <MultimatchDetailsContainer /> : null}
        {isEnded ? (
          <>
            <ResultTabs>
              <Tabs
                tabs={tabItems}
                onSelectionChange={handleTabChange}
                selected={selectedTab}
                tabHeight={2}
                customStyle={{
                  defaultBgColor: theme.DefaultTabs.default.backgroundColor,
                  defaultColor: theme.DefaultTabs.default.color,
                  selectedBgColor: theme.DefaultTabs.selected.backgroundColor,
                  selectedColor: theme.DefaultTabs.selected.color,
                }}
              />
            </ResultTabs>

            {renderMatchEndedTabs}
          </>
        ) : (
          <MatchGroupsWithBetFiltersContainer />
        )}
        {isEnded && !hallOfFameBetslips ? <StyledWinaLoader /> : null}
        <LineUpRouterSwitch />
        {Target.__MOBILE__ ? <MyMatchErrorPopUpContainer /> : null}
      </MatchViewContext.Provider>
    );
  }
  return null;
};

export default Match;
